import * as React from "react";
import { Search } from "react-bootstrap-icons";

import { BaseButton, IMemberPlan, IPlanType } from "../../../libs";
import * as Styled from "./PlanDetails.styled";

interface IProps {
  plans: IMemberPlan[];
  onCreatePlanClick: () => void;
  onEditPlanClick: (planId: string) => void;
  onDeletePlanClick: (planId: string) => void;
}

type SortKey = "name" | "type" | "access" | "visibility" | "status" | "amount";
type SortDirection = "ascending" | "descending";
type PlanFilter = "all" | "active" | "disabled" | "expired";

const dateOptions: Intl.DateTimeFormatOptions = {
  weekday: "short",
  year: "numeric",
  month: "short",
  day: "numeric",
};

const getPlanType = (plan: IMemberPlan) => {
  if (!plan.planType) return "—";
  return plan.planType === IPlanType.VOD ? "VOD" : "Live Stream";
};

const getStatus = (plan: IMemberPlan) => {
  if (!plan.active) return "Expired";
  return plan.isEnabled ? "Enabled" : "Disabled";
};

const getAccess = (plan: IMemberPlan) => {
  if (plan.isRecurring) return "Membership subscription";

  if (plan.expiration && plan.expiration !== "NONE") {
    if (isNaN(Date.parse(plan.expiration))) {
      const duration = plan.expiration.substring(0, plan.expiration.length - 1);
      return `Expires ${duration} days after purchase`;
    }

    const expiration = new Date(plan.expiration);
    return `Expires ${expiration.toLocaleDateString(
      "en-US",
      dateOptions
    )} at ${expiration.toLocaleTimeString()}`;
  }

  return "No expiration";
};

const getSortValue = (plan: IMemberPlan, sortKey: SortKey) => {
  switch (sortKey) {
    case "type":
      return getPlanType(plan);
    case "access":
      return getAccess(plan);
    case "visibility":
      return plan.isPublic ? "Public" : "Private";
    case "status":
      return getStatus(plan);
    case "amount":
      return plan.amount;
    default:
      return plan.name;
  }
};

export const PlanDetails: React.FC<IProps> = ({
  plans,
  onCreatePlanClick,
  onEditPlanClick,
  onDeletePlanClick,
}) => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [planFilter, setPlanFilter] = React.useState<PlanFilter>("all");
  const [sortKey, setSortKey] = React.useState<SortKey>("name");
  const [sortDirection, setSortDirection] = React.useState<SortDirection>("ascending");

  const visiblePlans = React.useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
    const filteredPlans = plans.filter((plan) => {
      const matchesFilter =
        planFilter === "all" ||
        (planFilter === "active" && Boolean(plan.active && plan.isEnabled)) ||
        (planFilter === "disabled" && Boolean(plan.active && !plan.isEnabled)) ||
        (planFilter === "expired" && !plan.active);

      if (!matchesFilter) return false;
      if (!normalizedQuery) return true;

      return [
        plan.name,
        plan.description,
        getPlanType(plan),
        getAccess(plan),
        plan.isPublic ? "Public" : "Private",
        getStatus(plan),
        plan.amount.toFixed(2),
      ]
        .join(" ")
        .toLocaleLowerCase()
        .includes(normalizedQuery);
    });

    return [...filteredPlans].sort((firstPlan, secondPlan) => {
      const firstValue = getSortValue(firstPlan, sortKey);
      const secondValue = getSortValue(secondPlan, sortKey);
      const result =
        typeof firstValue === "number" && typeof secondValue === "number"
          ? firstValue - secondValue
          : String(firstValue).localeCompare(String(secondValue), undefined, {
              numeric: true,
              sensitivity: "base",
            });

      return sortDirection === "ascending" ? result : -result;
    });
  }, [planFilter, plans, searchQuery, sortDirection, sortKey]);

  const handleSort = (nextSortKey: SortKey) => {
    if (nextSortKey === sortKey) {
      setSortDirection((currentDirection) =>
        currentDirection === "ascending" ? "descending" : "ascending"
      );
      return;
    }

    setSortKey(nextSortKey);
    setSortDirection("ascending");
  };

  const renderSortHeader = (label: string, key: SortKey) => (
    <Styled.SortButton
      type="button"
      onClick={() => handleSort(key)}
      aria-label={`Sort by ${label}`}
    >
      {label}
      <Styled.SortIndicator $active={sortKey === key} aria-hidden="true">
        {sortKey === key ? (sortDirection === "ascending" ? "▲" : "▼") : "↕"}
      </Styled.SortIndicator>
    </Styled.SortButton>
  );

  return (
    <Styled.PlanDetailsContainer fluid>
      <Styled.Header>
        <Styled.Subtitle>My Plans</Styled.Subtitle>
        <Styled.CreateButtonWrapper>
          <BaseButton onClick={onCreatePlanClick}>Create Plan</BaseButton>
        </Styled.CreateButtonWrapper>
      </Styled.Header>

      {plans.length === 0 ? (
        <Styled.EmptyState>
          There are no plans created. Create a plan to get started.
        </Styled.EmptyState>
      ) : (
        <>
          <Styled.TableToolbar>
            <Styled.SearchWrapper>
              <Styled.SearchIcon aria-hidden="true">
                <Search size={16} />
              </Styled.SearchIcon>
              <Styled.SearchInput
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search plans"
                aria-label="Search plans"
              />
            </Styled.SearchWrapper>
            <Styled.ToolbarControls>
              <Styled.FilterSelect
                value={planFilter}
                onChange={(event) => setPlanFilter(event.target.value as PlanFilter)}
                aria-label="Filter plans by status"
              >
                <option value="all">All plans</option>
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
                <option value="expired">Expired</option>
              </Styled.FilterSelect>
              <Styled.ResultCount aria-live="polite">
                {visiblePlans.length} of {plans.length} plan
                {plans.length === 1 ? "" : "s"}
              </Styled.ResultCount>
            </Styled.ToolbarControls>
          </Styled.TableToolbar>

          <Styled.TableContainer>
            <Styled.PlansTable>
              <thead>
                <tr>
                  <th aria-sort={sortKey === "name" ? sortDirection : "none"}>
                    {renderSortHeader("Plan", "name")}
                  </th>
                  <th aria-sort={sortKey === "type" ? sortDirection : "none"}>
                    {renderSortHeader("Type", "type")}
                  </th>
                  <th aria-sort={sortKey === "access" ? sortDirection : "none"}>
                    {renderSortHeader("Access", "access")}
                  </th>
                  <th aria-sort={sortKey === "visibility" ? sortDirection : "none"}>
                    {renderSortHeader("Visibility", "visibility")}
                  </th>
                  <th aria-sort={sortKey === "status" ? sortDirection : "none"}>
                    {renderSortHeader("Status", "status")}
                  </th>
                  <th aria-sort={sortKey === "amount" ? sortDirection : "none"}>
                    {renderSortHeader("Price", "amount")}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visiblePlans.length > 0 ? (
                  visiblePlans.map((plan) => (
                    <Styled.PlanRow $muted={!plan.active} key={plan.id}>
                      <td>
                        <Styled.PlanName $muted={!plan.active}>{plan.name}</Styled.PlanName>
                        {plan.description && (
                          <Styled.PlanDescription>{plan.description}</Styled.PlanDescription>
                        )}
                      </td>
                      <td>{getPlanType(plan)}</td>
                      <td>{getAccess(plan)}</td>
                      <td>
                        <Styled.Badge $variant={plan.isPublic ? "success" : "neutral"}>
                          {plan.isPublic ? "Public" : "Private"}
                        </Styled.Badge>
                      </td>
                      <td>
                        <Styled.Badge
                          $variant={
                            !plan.active ? "danger" : plan.isEnabled ? "success" : "neutral"
                          }
                        >
                          {getStatus(plan)}
                        </Styled.Badge>
                      </td>
                      <Styled.PriceCell>
                        $
                        {plan.amount.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Styled.PriceCell>
                      <td>
                        <Styled.Actions>
                          <Styled.ActionButton
                            type="button"
                            onClick={() => onEditPlanClick(plan.id)}
                          >
                            Edit
                          </Styled.ActionButton>
                          {(!plan.active || (!plan.isEnabled && !plan.isPublic)) && (
                            <Styled.ActionButton
                              type="button"
                              onClick={() => onDeletePlanClick(plan.id)}
                            >
                              Delete
                            </Styled.ActionButton>
                          )}
                        </Styled.Actions>
                      </td>
                    </Styled.PlanRow>
                  ))
                ) : (
                  <tr>
                    <Styled.NoResultsCell colSpan={7}>
                      {searchQuery.trim()
                        ? `No plans match "${searchQuery.trim()}".`
                        : `No ${planFilter} plans found.`}
                    </Styled.NoResultsCell>
                  </tr>
                )}
              </tbody>
            </Styled.PlansTable>
          </Styled.TableContainer>
        </>
      )}
    </Styled.PlanDetailsContainer>
  );
};
